import { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  DocumentReference,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, db, storage } from "@/firebase";
import { formatPhoneNumber } from "@/utils/phone";
import SendStatusModal from "@/components/mail/SendStatusModal";

const DEFAULT_SIGNATURE = `--------------------------------------
通信資格のラーキャリ運営事務局
〒619-0237
京都府相楽郡精華町光台１丁目７
けいはんなプラザ11F

■お問合せ
Mail：info@lear-caree.jp
LINE：https://lin.ee/qarm6oop

https://lear-caree.jp`;

interface Mail {
  id: string;
  customerEmail: string;
  subject: string;
}

interface MailMessageDetail {
  id: string;
  from: string;
  to: string;
  subject?: string;
  body?: string;
  bodyPreview?: string;
}

interface MailTemplate {
  id: string;
  title: string;
  subject: string;
  body: string;
  attach?: string;
  sign?: string;
  sortNo?: number;
}

interface TemplateContext {
  order: Record<string, any>;
  user: Record<string, any>;
  shikaku: Record<string, any>;
  samplesend: Record<string, any>;
}

const ensureReplySubject = (subject: string) => {
  const trimmed = subject.trim();
  if (!trimmed) return "Re:";
  return trimmed.toLowerCase().startsWith("re:") ? trimmed : `Re: ${trimmed}`;
};

const normalizeValue = (value: any, key?: string, seen: WeakSet<object> = new WeakSet()): any => {
  if (value === null || value === undefined) return value;
  if (value instanceof Timestamp) return value.toDate().toLocaleString("ja-JP");
  if (value instanceof DocumentReference) return value.path || value.id;
  if (typeof value === "string" && key?.toLowerCase().includes("phone")) return formatPhoneNumber(value);
  if (typeof value === "object") {
    if (seen.has(value)) return "[Circular]";
    seen.add(value);
    if (Array.isArray(value)) return value.map((item) => normalizeValue(item, undefined, seen));
    return Object.fromEntries(Object.entries(value).map(([childKey, val]) => [childKey, normalizeValue(val, childKey, seen)]));
  }
  return value;
};

const getTemplateValue = (context: TemplateContext, keyPath: string) => {
  const pathParts = keyPath.split(".");
  if (pathParts.length < 2) return undefined;
  const [rootKey, ...rest] = pathParts;
  let current: any = context[rootKey as keyof TemplateContext];
  for (const part of rest) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== "object") return undefined;
    current = Object.prototype.hasOwnProperty.call(current, part) ? current[part] : undefined;
  }
  return current;
};

const formatTemplateValue = (value: unknown) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return Number.isNaN(value) ? "" : String(value);
  if (Array.isArray(value)) return value.map((item) => formatTemplateValue(item)).filter((item) => item !== "").join(", ");
  return String(normalizeValue(value));
};

const applyTemplateVariables = (templateText: string, context: TemplateContext) =>
  templateText.replace(/{{\s*([\w.]+)\s*}}/g, (_match, key) => formatTemplateValue(getTemplateValue(context, String(key))));

export default function MailComposePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const replyMailId = searchParams.get("mailId") || "";
  const replyMessageId = searchParams.get("messageId") || "";
  const orderId = searchParams.get("orderId") || "";
  const sampleSendId = searchParams.get("sampleSendId") || "";
  const [mail, setMail] = useState<Mail | null>(null);
  const [message, setMessage] = useState<MailMessageDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [templates, setTemplates] = useState<MailTemplate[]>([]);
  const [templateContext, setTemplateContext] = useState<TemplateContext | null>(null);
  const [attachFiles, setAttachFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [composeForm, setComposeForm] = useState({
    from: "",
    to: "",
    subject: "",
    cc: "",
    bcc: "",
    body: "",
    sign: DEFAULT_SIGNATURE,
    templateId: "",
  });

  const isReply = Boolean(replyMailId && replyMessageId);
  const resolveTemplateText = (text: string) => (templateContext ? applyTemplateVariables(text, templateContext) : text);

  useEffect(() => {
    const preloadTemplates = async () => {
      try {
        const snapshot = await getDocs(query(collection(db, "mail_templates"), orderBy("sortNo", "asc")));
        setTemplates(snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<MailTemplate, "id">) })));
      } catch (err) {
        console.error("Failed to load mail templates", err);
      }
    };
    preloadTemplates();
  }, []);

  useEffect(() => {
    if (isReply) return;
    let isMounted = true;

    const buildTemplateContext = async (
      orderSource: Record<string, any> | null,
      sampleSendSource: Record<string, any> | null
    ): Promise<TemplateContext> => {
      const orderContext = orderSource ? { ...orderSource } : {};
      const samplesendContext = sampleSendSource ? { ...sampleSendSource } : {};
      let userContext: Record<string, any> = {};
      let shikakuContext: Record<string, any> = {};

      const userRef = orderSource?.UserRef;
      if (userRef) {
        const resolvedRef = userRef.path ? userRef : doc(db, "User", userRef);
        const snapshot = await getDoc(resolvedRef);
        if (snapshot.exists()) userContext = snapshot.data() as Record<string, any>;
      }

      const shikakuRef = orderSource?.shikakuId;
      if (shikakuRef) {
        const resolvedRef = shikakuRef.path ? shikakuRef : doc(db, "Shikaku", shikakuRef);
        const snapshot = await getDoc(resolvedRef);
        if (snapshot.exists()) shikakuContext = snapshot.data() as Record<string, any>;
      }

      return { order: orderContext, user: userContext, shikaku: shikakuContext, samplesend: samplesendContext };
    };

    const preloadTemplateContext = async () => {
      setLoading(true);
      try {
        let orderContext: Record<string, any> | null = null;
        let sampleSendContext: Record<string, any> | null = null;

        if (orderId) {
          const snap = await getDoc(doc(db, "Order", orderId));
          if (snap.exists()) orderContext = { orderId: snap.id, ...(snap.data() as Record<string, any>) };
        }
        if (sampleSendId) {
          const snap = await getDoc(doc(db, "samplesend", sampleSendId));
          if (snap.exists()) sampleSendContext = { sampleSendId: snap.id, ...(snap.data() as Record<string, any>) };
        }

        const nextContext = await buildTemplateContext(orderContext, sampleSendContext);
        if (!isMounted) return;
        setTemplateContext(nextContext);

        const nextEmail = nextContext.user?.email || nextContext.samplesend?.email || "";
        if (nextEmail) setComposeForm((prev) => ({ ...prev, to: prev.to || nextEmail }));
      } catch (err) {
        console.error("Failed to load template context", err);
        setSendError("テンプレート情報の取得に失敗しました。");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    preloadTemplateContext();
    return () => { isMounted = false; };
  }, [orderId, sampleSendId, isReply]);

  useEffect(() => {
    const currentFrom = auth.currentUser?.email || "";
    if (!replyMailId || !replyMessageId) {
      setComposeForm((prev) => ({ ...prev, from: currentFrom || prev.from }));
      return;
    }

    let isMounted = true;
    const loadReplyData = async () => {
      setLoading(true);
      try {
        const [mailSnapshot, messageSnapshot] = await Promise.all([
          getDoc(doc(db, "mail", replyMailId)),
          getDoc(doc(db, "mail_messages", replyMessageId)),
        ]);
        if (!isMounted) return;

        const mailData = mailSnapshot.exists() ? ({ id: mailSnapshot.id, ...(mailSnapshot.data() as Mail) } as Mail) : null;
        const messageData = messageSnapshot.exists() ? ({ id: messageSnapshot.id, ...(messageSnapshot.data() as MailMessageDetail) } as MailMessageDetail) : null;

        setMail(mailData);
        setMessage(messageData);
        setComposeForm((prev) => ({
          ...prev,
          from: currentFrom || prev.from,
          to: messageData?.from || mailData?.customerEmail || prev.to,
          subject: messageData?.subject
            ? ensureReplySubject(messageData.subject)
            : mailData?.subject
            ? ensureReplySubject(mailData.subject)
            : prev.subject,
        }));
      } catch (err) {
        console.error("Failed to load reply data", err);
        setSendError("返信データの取得に失敗しました。");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadReplyData();
    return () => { isMounted = false; };
  }, [replyMailId, replyMessageId]);

  useEffect(() => {
    if (!templateContext || !composeForm.templateId) return;
    const selectedTemplate = templates.find((t) => t.id === composeForm.templateId);
    if (!selectedTemplate) return;
    setComposeForm((prev) => ({
      ...prev,
      subject: selectedTemplate.subject ? resolveTemplateText(selectedTemplate.subject) : prev.subject,
      body: selectedTemplate.body ? resolveTemplateText(selectedTemplate.body) : prev.body,
      sign: selectedTemplate.sign ? resolveTemplateText(selectedTemplate.sign) : prev.sign,
    }));
  }, [templateContext, composeForm.templateId, templates]);

  const replyContext = useMemo(() => {
    if (!isReply) return null;
    return {
      from: message?.from || mail?.customerEmail || "-",
      subject: mail?.subject || message?.subject || "-",
      body: message?.body || message?.bodyPreview || "",
    };
  }, [isReply, mail, message]);

  const handleSend = async () => {
    const body = composeForm.body.trim();
    const from = (composeForm.from || auth.currentUser?.email || "").trim();
    const to = composeForm.to.trim();
    const subject = composeForm.subject.trim();
    const cc = composeForm.cc.trim();
    const bcc = composeForm.bcc.trim();
    const sign = composeForm.sign.trim();

    if (!body || !to) {
      setSendError("To / 本文 は必須です。");
      return;
    }

    setSending(true);
    setSendError(null);
    setSendStatus("sending");
    try {
      const currentUser = auth.currentUser;
      let mailRef = replyMailId ? doc(db, "mail", replyMailId) : null;

      if (!mailRef) {
        const mailDoc = await addDoc(collection(db, "mail"), {
          customerEmail: to,
          subject: subject || "(no subject)",
          status: "waiting",
          spamLevel: 0,
          createdAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
        });
        mailRef = mailDoc;
      } else {
        await updateDoc(mailRef, {
          lastMessageAt: serverTimestamp(),
          status: "waiting",
          subject: subject || mail?.subject || "(no subject)",
        });
      }

      // 添付ファイルをCloud Storageにアップロード
      const tempId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const uploadedAttachments: Array<{ fileName: string; storagePath: string; contentType: string; size: number }> = [];
      for (const file of attachFiles) {
        const safeName = file.name.replace(/[^\w.\-]/g, "_");
        const storagePath = `mail_attachments_out/${tempId}/${safeName}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file, { contentType: file.type || "application/octet-stream" });
        uploadedAttachments.push({ fileName: file.name, storagePath, contentType: file.type || "application/octet-stream", size: file.size });
      }

      const bodyWithSignature = sign ? `${body}\n\n${sign}` : body;
      await addDoc(collection(db, "mail_messages"), {
        mailId: mailRef,
        direction: "out",
        from,
        to,
        subject: subject || "(no subject)",
        cc,
        bcc,
        body: bodyWithSignature,
        sign,
        staffUserId: currentUser?.uid,
        createdAt: serverTimestamp(),
        attachments: uploadedAttachments.length > 0 ? uploadedAttachments : [],
      });

      setSendStatus("success");
    } catch (err) {
      console.error("Failed to send mail message", err);
      setSendError("送信に失敗しました。");
      setSendStatus("error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-8">
      {sendStatus !== "idle" && (
        <SendStatusModal
          status={sendStatus}
          errorMessage={sendStatus === "error" ? sendError : null}
          onClose={() => {
            if (sendStatus === "success") {
              navigate(-1);
            } else {
              setSendStatus("idle");
            }
          }}
        />
      )}
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <button type="button" className="text-sm text-blue-600 hover:underline" onClick={() => navigate("/admin/mail")}>
              ← メール一覧に戻る
            </button>
            <h1 className="mt-2 text-2xl font-bold text-gray-800">
              {isReply ? "返信メールの作成" : "新規メール作成"}
            </h1>
            <p className="text-sm text-gray-500">SMTP 送信は mail_messages 追加後に Cloud Functions で実行されます。</p>
          </div>
        </div>

        {replyContext && (
          <div className="rounded-lg border bg-white p-4 text-sm text-gray-700 shadow-sm">
            <div className="text-xs font-semibold text-gray-500">返信元のメール</div>
            <div className="mt-2 space-y-1">
              <div>From: {replyContext.from}</div>
              <div>件名: {replyContext.subject}</div>
              {replyContext.body && (
                <div className="mt-2 rounded border border-dashed border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 whitespace-pre-wrap">
                  {replyContext.body}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="rounded-lg border bg-white p-6 shadow-sm space-y-4">
          {/* テンプレート */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700" htmlFor="compose-template">テンプレート</label>
            <select
              id="compose-template"
              className="w-full rounded-lg border px-3 py-2"
              value={composeForm.templateId}
              onChange={(e) => {
                const selectedId = e.target.value;
                const selectedTemplate = templates.find((t) => t.id === selectedId);
                setComposeForm((prev) => ({
                  ...prev,
                  templateId: selectedId,
                  subject: selectedTemplate?.subject ? resolveTemplateText(selectedTemplate.subject) : prev.subject,
                  body: selectedTemplate?.body ? resolveTemplateText(selectedTemplate.body) : prev.body,
                  sign: selectedTemplate?.sign ? resolveTemplateText(selectedTemplate.sign) : prev.sign,
                }));
              }}
              disabled={loading}
            >
              <option value="">テンプレートを選択</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
            <p className="text-xs text-gray-500">テンプレートの本文には {"{{"}変数{"}}"} を含められます。送信前に必要に応じて書き換えてください。</p>
          </div>

          {/* To */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700" htmlFor="compose-to">To</label>
            <input
              id="compose-to"
              type="email"
              className="w-full rounded-lg border px-3 py-2"
              value={composeForm.to}
              onChange={(e) => setComposeForm((prev) => ({ ...prev, to: e.target.value }))}
              placeholder="受信者アドレス"
              disabled={loading}
            />
          </div>

          {/* CC / BCC */}
          <div className="space-y-2">
            <button
              type="button"
              className="flex items-center gap-2 text-sm font-medium text-blue-600"
              onClick={() => setShowCcBcc((prev) => !prev)}
              disabled={loading}
            >
              <span className={`transition-transform ${showCcBcc ? "rotate-90" : "rotate-0"}`}>▶</span>
              CC / BCC
            </button>
            {showCcBcc && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="compose-cc">CC</label>
                  <input id="compose-cc" type="text" className="w-full rounded-lg border px-3 py-2"
                    value={composeForm.cc} onChange={(e) => setComposeForm((prev) => ({ ...prev, cc: e.target.value }))}
                    placeholder="CC を入力" disabled={loading} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="compose-bcc">BCC</label>
                  <input id="compose-bcc" type="text" className="w-full rounded-lg border px-3 py-2"
                    value={composeForm.bcc} onChange={(e) => setComposeForm((prev) => ({ ...prev, bcc: e.target.value }))}
                    placeholder="BCC を入力" disabled={loading} />
                </div>
              </div>
            )}
          </div>

          {/* 件名 */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700" htmlFor="compose-subject">件名</label>
            <input
              id="compose-subject"
              type="text"
              className="w-full rounded-lg border px-3 py-2"
              value={composeForm.subject}
              onChange={(e) => setComposeForm((prev) => ({ ...prev, subject: e.target.value }))}
              placeholder="件名を入力"
              disabled={loading}
            />
          </div>

          {/* 本文 */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700" htmlFor="compose-body">本文</label>
            <textarea
              id="compose-body"
              className="w-full min-h-[220px] rounded-lg border px-3 py-2 resize"
              value={composeForm.body}
              onChange={(e) => setComposeForm((prev) => ({ ...prev, body: e.target.value }))}
              placeholder="本文を入力してください"
              disabled={loading}
            />
          </div>

          {/* 署名 */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700" htmlFor="compose-sign">署名</label>
            <input
              id="compose-sign"
              type="text"
              className="w-full rounded-lg border px-3 py-2"
              value={composeForm.sign}
              onChange={(e) => setComposeForm((prev) => ({ ...prev, sign: e.target.value }))}
              placeholder="署名を入力"
              disabled={loading}
            />
          </div>

          {/* 添付ファイル */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">添付ファイル</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50"
              >
                📎 ファイルを選択
              </button>
              <span className="text-xs text-gray-400">{attachFiles.length === 0 ? "未選択" : `${attachFiles.length}件選択済み`}</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const newFiles = Array.from(e.target.files || []);
                setAttachFiles((prev) => [...prev, ...newFiles]);
                e.target.value = "";
              }}
            />
            {attachFiles.length > 0 && (
              <ul className="space-y-1">
                {attachFiles.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                    <span className="text-gray-400">📎</span>
                    <span className="text-sm text-gray-700 flex-1 truncate">{f.name}</span>
                    <span className="text-xs text-gray-400">{(f.size / 1024).toFixed(1)} KB</span>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-500 text-xs ml-1"
                      onClick={() => setAttachFiles((prev) => prev.filter((_, idx) => idx !== i))}
                    >✕</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {sendError && <div className="text-sm text-red-600">{sendError}</div>}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              className="rounded border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
              onClick={() => navigate("/admin/mail")}
              disabled={sending}
            >
              キャンセル
            </button>
            <button
              className="rounded bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700 disabled:opacity-60"
              onClick={handleSend}
              disabled={sending || loading}
            >
              {sending ? "送信中..." : "送信する"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
